









































































































import { defineComponent, ref, watch, computed, PropType } from '@vue/composition-api';
import { useAssetMetadata } from '../composable/asset-metadata';
import { Model } from '@/app/interfaces';
import { ModelsAPI } from '@/modules/data-model/api';
import { useAxios } from '@/app/composable';
import { AssetsAPI } from '../api';
import { SelfBuildingSquareSpinner } from 'epic-spinners';
import AlertBanner from '@/app/components/AlertBanner.vue';
import DescriptionListCard from '@/app/components/DescriptionListCard.vue';
import { FormCard } from '@/app/components';
import JsonEditor from '@/app/components/JsonEditor.vue';
import Scrollbar from '@/app/components/Scrollbar.vue';
import { CloudDownloadIcon } from '@vue-hero-icons/outline';
import { useBlob } from '@/app/composable';
import DataModelTree from '@/app/components/datamodel-tree/DataModelTree.vue';
import { Asset } from '../types';

export default defineComponent({
    name: 'DataStructureTab',
    props: {
        asset: {
            type: Object as PropType<Asset>,
            required: true,
        },
    },
    components: {
        SelfBuildingSquareSpinner,
        AlertBanner,
        DescriptionListCard,
        FormCard,
        JsonEditor,
        Scrollbar,
        CloudDownloadIcon,
        DataModelTree,
    },
    setup(props) {
        const SAMPLE_FIELDS_LIMIT = 500;

        const loadingProcessedSample = ref<boolean>(false);
        const loadingFlatModel = ref<boolean>(false);
        const flatModel = ref<any>(null);
        const processedSample = ref<any>(null);
        const loadSample = ref<boolean>(true);
        const dataStructure = ref<any>(null);

        const { exec } = useAxios();
        const { download } = useBlob();
        const { getDomain, createTreeStructure } = useAssetMetadata();

        const loadDataStructure = async () => {
            const primaryConcept = props.asset.structure?.primaryConcept;
            const schema = props.asset.structure?.schema;
            if (primaryConcept && schema && schema.length > 0)
                dataStructure.value = createTreeStructure(schema, primaryConcept, flatModel.value);
        };

        const downloadProcessedSample = () =>
            download([JSON.stringify(processedSample.value, null, '\t')], 'processedSample.json');

        const deepFlattenToObject = (obj: any, prefix = '', seperator = '_') => {
            return Object.keys(obj).reduce((acc, k) => {
                if (typeof obj[k] === 'object' && obj[k] !== null) {
                    Object.assign(acc, deepFlattenToObject(obj[k], prefix + k + seperator));
                } else {
                    acc[prefix + k] = obj[k];
                }
                return acc;
            }, {});
        };

        /**
         * Computes a cropped sample based on the flattened representation of a nested sample.
         *
         * This function takes a nested sample, flattens it, and computes a cropped version
         * based on the specified field limit. If the flattened sample has fewer or equal
         * fields than the limit, the original sample is returned. Otherwise, the sample
         *
         * is cropped to include fields up to the specified limit.
         * @returns The computed cropped sample.
         */
        const croppedSample = computed(() => {
            if (!processedSample.value || !processedSample.value?.length) return processedSample.value;
            const flattenedSample = deepFlattenToObject(processedSample.value);
            const keys = Object.keys(flattenedSample);
            if (keys.length <= SAMPLE_FIELDS_LIMIT) return processedSample.value;

            // during flattening, each key is prefixed by the index in the proccessedSample array e.g, "24_Building_heatingDatetime"
            // to handle samples with a large number of fields or rows, if we reach the key at the SAMPLE_FIELDS_LIMIT position,
            // we can extract the original index from the key.
            // This allows us to truncate the sample to stay within the specified limit.
            const lastRow = parseInt(keys[SAMPLE_FIELDS_LIMIT].split('_')[0]); // e.g. lastRow is 24 for "24_Building_heatingDatetime"
            if (lastRow === 0) {
                // we can't even display a single record so don't display anything
                loadSample.value = false;
                return [];
            }
            return processedSample.value.slice(0, lastRow);
        });

        watch(
            () => flatModel.value,
            () => loadDataStructure(),
        );

        watch(
            () => props.asset,
            async (newAsset) => {
                if (newAsset.structure?.domain && newAsset.structure?.primaryConcept) {
                    const { majorVersion, uid } = newAsset.structure.domain;
                    const domain: Model = await getDomain(uid, majorVersion);

                    if (domain) {
                        loadingFlatModel.value = true;
                        try {
                            const res = await ModelsAPI.conceptNames(domain.id);
                            flatModel.value = res.data;
                            loadingFlatModel.value = false;
                        } catch {
                            loadingFlatModel.value = false;
                        }
                    }

                    loadingProcessedSample.value = true;
                    exec(AssetsAPI.getProcessedSample(newAsset.id))
                        .then((res: any) => {
                            processedSample.value = res.data;
                            loadingProcessedSample.value = false;
                        })
                        .catch(() => (loadingProcessedSample.value = false));
                }
            },
            { immediate: true },
        );

        return {
            loadingFlatModel,
            loadingProcessedSample,
            loadSample,
            processedSample,
            dataStructure,

            downloadProcessedSample,

            croppedSample,
        };
    },
});
